import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Plus, Ban } from "lucide-react";
import { api, errMsg, fmtDate, BACKEND } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Kopiert"), () => toast.error("Kopieren nicht möglich"));

export default function ApiAccess() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const load = () => api.get("/hub-keys").then((r) => setKeys(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/hub-keys", { name });
      setFresh(data); setName(""); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const revoke = async (id) => { await api.delete(`/hub-keys/${id}`); toast.success("Schlüssel widerrufen"); load(); };
  const curl = `curl ${BACKEND}/api/v1/chat/completions \\
  -H "Authorization: Bearer ${fresh?.key || "chub_DEIN_HUB_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"auto","messages":[{"role":"user","content":"Hallo!"}]}'`;

  return (
    <div data-testid="api-access-page">
      <PageHeader title="API-Zugang" subtitle="Persönliche Hub-Schlüssel für eigene Apps. Der Endpunkt ist OpenAI-kompatibel; „model“: „auto“ nutzt Free-First-Routing, „provider:modell“ wählt gezielt. Kostenpflichtige Routen nur mit „allow_paid“: true." />
      <div className="grid gap-6 xl:grid-cols-2">
        <section className="glass p-6">
          <form onSubmit={create} className="flex gap-3">
            <input data-testid="hubkey-name-input" required maxLength={60} className="field" placeholder="Name, z. B. Meine App" value={name} onChange={(e) => setName(e.target.value)} />
            <button data-testid="hubkey-create-button" className="btn-grad inline-flex items-center gap-1.5 whitespace-nowrap px-4"><Plus className="h-4 w-4" />Erstellen</button>
          </form>
          {fresh && (
            <div className="mt-5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4" data-testid="hubkey-fresh">
              <div className="text-sm font-semibold text-amber-200">Nur jetzt sichtbar – sicher aufbewahren:</div>
              <div className="mt-2 flex items-center gap-2"><code className="flex-1 break-all rounded bg-black/40 px-2 py-1 text-xs text-white" data-testid="hubkey-fresh-value">{fresh.key}</code>
                <button onClick={() => copy(fresh.key)} className="btn-ghost p-2" data-testid="hubkey-copy-button"><Copy className="h-4 w-4" /></button></div>
            </div>
          )}
          <div className="mt-6 space-y-2" data-testid="hubkey-list">
            {keys.length === 0 ? <EmptyState icon={KeyRound} title="Noch keine Schlüssel" text="Erstelle einen Schlüssel, um das Gateway aus eigenen Apps zu nutzen." /> :
              keys.map((k) => (
                <div key={k.id} className="flex items-center justify-between rounded-lg border border-white/5 px-4 py-3" data-testid={`hubkey-row-${k.id}`}>
                  <div><div className="text-sm font-semibold text-white">{k.name} <span className="font-mono text-xs text-slate-500">{k.prefix}…</span></div>
                    <div className="text-xs text-slate-500">Erstellt {fmtDate(k.created_at)} · Zuletzt genutzt {fmtDate(k.last_used_at)}</div></div>
                  {k.revoked ? <span className="text-xs text-red-300">Widerrufen</span> :
                    <button onClick={() => revoke(k.id)} data-testid={`hubkey-revoke-${k.id}`} className="btn-ghost inline-flex items-center gap-1 px-3 py-1.5 text-xs text-red-300"><Ban className="h-3.5 w-3.5" />Widerrufen</button>}
                </div>
              ))}
          </div>
        </section>
        <section className="glass overflow-hidden">
          <div className="flex items-center justify-between border-b border-white/5 px-5 py-3 text-xs text-slate-400">Beispielaufruf (cURL)<button onClick={() => copy(curl)} className="btn-ghost p-1.5" data-testid="api-example-copy"><Copy className="h-3.5 w-3.5" /></button></div>
          <pre className="overflow-x-auto p-5 text-xs leading-relaxed text-slate-300" data-testid="api-example-code">{curl}</pre>
          <div className="border-t border-white/5 p-5 text-xs text-slate-400 space-y-1">
            <div><b className="text-slate-200">Antwort:</b> OpenAI-Format + Feld <code>casi</code> mit request_id, route, attempts, duration_ms.</div>
            <div><b className="text-slate-200">Fehler:</b> <code>{'{"error":{"code","message","request_id"}}'}</code> – z. B. DAILY_LIMIT_REACHED, PAID_ROUTE_REQUIRES_CONFIRMATION, NO_FREE_ROUTE_AVAILABLE.</div>
            <div><b className="text-slate-200">Streaming:</b> <code>"stream": true</code> (SSE, ohne Fallback).</div>
            <div><b className="text-slate-200">Doku:</b> <a href={`${BACKEND}/api/docs`} target="_blank" rel="noreferrer" className="text-blue-400">OpenAPI /api/docs</a></div>
          </div>
        </section>
      </div>
    </div>
  );
}
